import { ListEventOptions, ListEventsResponseEntry } from '@shared/core/events'
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { useHandleRetry } from 'src/modules/core/hooks/use-handle-retry'
import { LIST_USER_EVENTS_CACHE_KEY } from 'src/modules/user-events/use-list-user-events'
import { ApiService } from 'src/services/api.service'

export const GET_EVENT_LIST_CACHE_KEY = 'get-event-list'

/**
 * The value exposed by `useEventList` as `data`: the entries array with the
 * optional truncation metadata carried alongside it. Consumers that only need
 * the entries keep treating it as an array; the dashboard reads `truncated` to
 * surface a warning when the server capped the result set.
 */
export type EventListData = ListEventsResponseEntry[] & {
  truncated?: boolean
  total_in_range?: number
  returned_count?: number
}

export function useEventList(
  params?: ListEventOptions,
  options?: UseQueryOptions<EventListData, unknown, EventListData>,
) {
  const handleRetry = useHandleRetry()
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options || {}
  return useQuery<EventListData, unknown, EventListData>(
    [GET_EVENT_LIST_CACHE_KEY, params],
    () => ApiService.Events.getEventList(params),
    {
      retry: handleRetry,
      // Keep the cache array-shaped. React Query's default structural sharing
      // (`replaceEqualDeep`) copies arrays index-by-index and drops extra
      // own-properties, which would strip the `truncated`/`total_in_range`/
      // `returned_count` metadata the HTTP client attaches to the entries
      // array. Disabling it lets the cache stay an array-with-metadata, which
      // is the invariant optimistic updaters rely on: they call
      // `queryClient.setQueryData(key, changeArrayElementValues(...))`, whose
      // `prev.map` only works when `prev` is an array (a wrapper object would
      // abort the mutation). Note: after such an optimistic update the fresh
      // `.map()` array loses the metadata props until the next refetch, so the
      // truncation warning may briefly blink; that is acceptable.
      structuralSharing: false,
      onSuccess: (data) => {
        if (data && data.length === 0) {
          queryClient.invalidateQueries([LIST_USER_EVENTS_CACHE_KEY])
        }
        onSuccess?.(data)
      },
      ...restOptions,
    },
  )
}
